import React, { useContext, useEffect, useRef, useState } from "react";
import { AppContextType, WonmovContext } from "@/context/app-context";
import { videoRepo } from "@/repo/video-repo";
import { MovieCommentData } from "@/entry/server/movie";
import { Pagination } from "@/lib/api";
import likeIcon from "@public/img/like.svg";
import likeFillIcon from "@public/img/like-fill.svg";
import dislikeIcon from "@public/img/dislike.svg";
import dislikeFillIcon from "@public/img/dislike-fill.svg";
import CloseIcon from "@public/img/close.svg";
import UserWaveIcon from "@public/img/user-waves.svg";
import TextareaField from "../../widgets/textarea-field";
import Tabs from "../../commom/tabs";
import TextField from "../../widgets/text-field";
import "@/styles/comments.css";

const TABS = ["دیدگاه ها", "نقد و بررسی ها"];

export default function Comments({ movieId, defaultComments, commentsPagination }: { movieId: number, defaultComments: MovieCommentData[], commentsPagination: Pagination }) {
    const { isLoggedIn, setNotification } = useContext<AppContextType>(WonmovContext);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [comment, setComment] = useState('');
    const [isSpoiler, setIsSpoiler] = useState(false);
    const [isReview, setIsReview] = useState(false);
    const [score, setScore] = useState('');
    const [comments, setComments] = useState<MovieCommentData[]>(defaultComments);
    const [pagination, setPagination] = useState<Pagination>(commentsPagination);
    const [replyingComment, setReplyingComment] = useState<MovieCommentData | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [selectedTab, setSelectedTab] = useState(0);

    useEffect(function () {
        if (replyingComment && textareaRef.current) {
            textareaRef.current.scrollIntoView();
            textareaRef.current.focus();
        }
    }, [replyingComment]);

    useEffect(function () {
        getMoreComments(true);
    }, [selectedTab]);

    const getMoreComments = async function (reset?: boolean) {
        if (reset === undefined) {
            reset = false;
        }
        if (loadingMore && !reset) return;
        setLoadingMore(true);
        const page = reset ? 1 : pagination.next_page;
        try {
            const response = await videoRepo.getComments(movieId, selectedTab === 0 ? "false" : "true", page);
            setLoadingMore(false);
            if (response.status) {
                setComments(reset ? (response.data ? response.data : []) : function (prev) {
                    return prev.concat(response.data ? response.data : []);
                });
                if (response.pagination) {
                    setPagination(response.pagination);
                }
            }
        } catch (e) {
            setLoadingMore(false);
        }
    };

    const handleSubmit = async () => {
        if (loading) return;

        if (!isLoggedIn) {
            setNotification({ type: 'danger', message: 'برای ارسال دیدگاه باید وارد حساب کاربری خود شوید' });
            return;
        }
        if (comment.trim() === '') {
            setNotification({ type: 'danger', message: 'دیدگاه نمی تواند خالی باشد' });
            return;
        }
        if (isReview && !score) {
            setNotification({ type: 'danger', message: 'امتیاز نمی تواند خالی باشد' });
            return;
        }
        if (isReview && (Number(score) < 1 || Number(score) > 10)) {
            setNotification({ type: 'danger', message: 'امتیاز باید بین 1 و 10 باشد' });
            return;
        }
        setLoading(true);
        const response = await videoRepo.addComment(
            movieId,
            comment,
            replyingComment ? replyingComment.id : 0,
            isSpoiler,
            isReview,
            score ? Number(score) : undefined
        );
        setLoading(false);
        if (response.status) {
            setComment('');
            setScore('');
            setReplyingComment(null);
            setIsSpoiler(false);
            setIsReview(false);
            setNotification({ type: 'success', message: 'دیدگاه با موفقیت ارسال شد و پس از تایید نمایش داده خواهد شد' });
        } else {
            setNotification({ type: 'danger', message: response.message });
        }
    }

    const likeComment = async (commentId: number) => {
        if (!isLoggedIn) {
            setNotification({ type: 'danger', message: 'برای لایک کردن دیدگاه باید وارد حساب کاربری خود شوید' });
            return;
        }

        if (loading) return;
        var targetComment = comments.find(function (c) { return c.id === commentId; });
        if (targetComment && targetComment.is_liked) return;
        const response = await videoRepo.likeComment(commentId, 'like');
        if (response.status) {
            const editedComment = comments.find(comment => comment.id === commentId);
            if (editedComment) {
                editedComment.like_count++;
                editedComment.is_liked = true;
                if (editedComment.is_disliked) {
                    editedComment.dislike_count--;
                    editedComment.is_disliked = false;
                }
            }
            setComments(prev => prev.map(comment => comment.id === commentId ? editedComment! : comment));
        }
    }
    const dislikeComment = async (commentId: number) => {
        if (!isLoggedIn) {
            setNotification({ type: 'danger', message: 'برای دوست نداشتن دیدگاه باید وارد حساب کاربری خود شوید' });
            return;
        }

        if (loading) return;
        var targetComment = comments.find(function (c) { return c.id === commentId; });
        if (targetComment && targetComment.is_disliked) return;
        const response = await videoRepo.likeComment(commentId, 'dislike');
        if (response.status) {
            const editedComment = comments.find(comment => comment.id === commentId);
            if (editedComment) {
                editedComment.dislike_count++;
                editedComment.is_disliked = true;
                if (editedComment.is_liked) {
                    editedComment.like_count--;
                    editedComment.is_liked = false;
                }
            }
            setComments(prev => prev.map(comment => comment.id === commentId ? editedComment! : comment));
        }
    }

    return (
        <div className="movie-comments">
            <h2 className="movie-detail-section-title">ارسال دیدگاه</h2>
            <section className="movie-comments__panel">
                {replyingComment && (
                    <div className="movie-comments__reply-banner">
                        <div className="movie-comments__reply-header">
                            <p className="movie-comments__reply-title">در حال پاسخ به {replyingComment.username}</p>
                            <img
                                src={CloseIcon}
                                alt="بستن"
                                className="movie-comments__reply-close"
                                onClick={function () { setReplyingComment(null); }}
                            />
                        </div>
                        <hr className="movie-comments__divider" />
                        <p className="movie-comments__reply-text">{replyingComment.comment}</p>
                    </div>
                )}

                <TextareaField
                    name="comment"
                    id="comment"
                    placeholder="دیدگاه خود را اینجا وارد کنید"
                    rows={8}
                    value={comment}
                    onChange={function (e) { setComment(e.target.value); }}
                    ref={textareaRef}
                    rootClassName="movie-comments__textarea"
                />

                <div className="movie-comments__form-bar">
                    <label className="movie-comments__check">
                        <input
                            type="checkbox"
                            name="isSpoiler"
                            id="isSpoiler"
                            checked={isSpoiler}
                            onChange={function (e) { setIsSpoiler(e.target.checked); }}
                        />
                        نظر حاوی اسپویل است؟
                    </label>
                    <label className="movie-comments__check">
                        <input
                            type="checkbox"
                            name="isReview"
                            id="isReview"
                            checked={isReview}
                            onChange={function (e) { setIsReview(e.target.checked); }}
                        />
                        نقد و بررسی است؟
                    </label>
                    <div className="movie-comments__score">
                        <TextField
                            name="score"
                            id="score"
                            placeholder="امتیاز (1 تا 10)"
                            type="number"
                            min={1}
                            max={10}
                            value={score}
                            onChange={function (e) { setScore(e.target.value); }}
                        />
                    </div>
                    <button
                        type="submit"
                        className="movie-comments__submit"
                        onClick={handleSubmit}
                        disabled={loading}
                    >
                        {loading ? <div className="loader" /> : "ارسال دیدگاه"}
                    </button>
                </div>

                <h2 className="movie-detail-section-title movie-comments__list-title">دیدگاه ها</h2>

                <Tabs tabs={TABS} selectedTab={selectedTab} onChange={setSelectedTab} />

                <div className="movie-comments__list">
                    {comments.map(function (item) {
                        if (selectedTab === 0) {
                            return (
                                <CommentItem
                                    key={item.id}
                                    comment={item}
                                    onReply={function () { setReplyingComment(item); }}
                                    onLike={function () { likeComment(item.id); }}
                                    onDislike={function () { dislikeComment(item.id); }}
                                />
                            );
                        }
                        return (
                            <ReviewItem
                                key={item.id}
                                review={item}
                                onLike={function () { likeComment(item.id); }}
                                onDislike={function () { dislikeComment(item.id); }}
                            />
                        );
                    })}
                </div>

                {pagination && pagination.has_more && (
                    <button
                        type="button"
                        className="movie-comments__load-more"
                        onClick={function () { getMoreComments(false); }}
                        disabled={loadingMore}
                    >
                        {loadingMore ? <div className="loader" /> : "نمایش بیشتر"}
                    </button>
                )}
            </section>
        </div>
    );
}

function CommentItem({ comment, onReply, onLike, onDislike }: { comment: MovieCommentData, onReply: () => void, onLike: () => void, onDislike: () => void }) {
    const [showText, setShowText] = useState(!comment.is_spoiler);

    var itemClass = "movie-comments__item";
    if (comment.parent_id > 0) {
        itemClass += " movie-comments__item--nested";
    }

    return (
        <div className={itemClass}>
            <div className="movie-comments__item-header">
                <p className="movie-comments__username">{comment.username}</p>
                <p className="movie-comments__date">{comment.created_at}</p>
                <button type="button" className="movie-comments__reply-btn" onClick={onReply}>پاسخ</button>
            </div>

            <hr className="movie-comments__divider" />

            <div className="movie-comments__item-body">
                <div className="movie-comments__text-wrap">
                    <p className="movie-comments__text">
                        {showText ? comment.comment : "این دیدگاه حاوی اسپویل است"}
                    </p>
                    {!showText && (
                        <button
                            type="button"
                            className="movie-comments__spoiler-btn"
                            onClick={function () { setShowText(true); }}
                        >
                            نمایش
                        </button>
                    )}
                </div>

                <div className="movie-comments__votes">
                    <span className="movie-comments__vote" onClick={onLike}>
                        <img src={comment.is_liked ? likeFillIcon : likeIcon} alt="لایک" />
                        {comment.like_count}+
                    </span>
                    <span className="movie-comments__vote" onClick={onDislike}>
                        <img src={comment.is_disliked ? dislikeFillIcon : dislikeIcon} alt="دیسلایک" />
                        {comment.dislike_count}-
                    </span>
                </div>
            </div>
        </div>
    )
}

function ReviewItem({
    review,
    onLike,
    onDislike,
}: {
    review: MovieCommentData;
    onLike: () => void;
    onDislike: () => void;
}) {
    const [showText, setShowText] = useState(!review.is_spoiler);

    return (
        <div className="movie-comments__item">
            <div className="movie-comments__item-header">
                <p className="movie-comments__username">{review.username}</p>
                <p className="movie-comments__date">{review.created_at}</p>
            </div>

            <hr className="movie-comments__divider" />

            <div className="movie-comments__review-body">
                <div className="movie-comments__review-text-wrap">
                    <p className="movie-comments__review-text">
                        {showText ? review.comment : "این نقد حاوی اسپویل است"}
                    </p>
                    {!showText && (
                        <button
                            type="button"
                            className="movie-comments__spoiler-btn"
                            onClick={function () { setShowText(true); }}
                        >
                            نمایش
                        </button>
                    )}
                </div>

                <div className="movie-comments__review-side">
                    <div className="movie-comments__review-score">
                        <img src={UserWaveIcon} alt="" />
                        <p className="movie-comments__review-score-text">
                            <span className="movie-comments__review-score-value">{review.score}</span> / 10
                        </p>
                    </div>
                    <div className="movie-comments__review-votes">
                        <button type="button" className="movie-comments__review-vote" onClick={onLike}>
                            {review.like_count}+
                            <img src={review.is_liked ? likeFillIcon : likeIcon} alt="لایک" />
                        </button>
                        <button type="button" className="movie-comments__review-vote" onClick={onDislike}>
                            {review.dislike_count}-
                            <img src={review.is_disliked ? dislikeFillIcon : dislikeIcon} alt="دیسلایک" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}